import { useEffect, useState } from 'react';

import { Grid } from '@mantine/core';
import { useSelector } from 'react-redux';

import { RootState } from '@/store';
import { selectPatientDetails } from '@/store/selectors/transmissionReportPatientSelectors';
import {
  useGetAuthorizedPractitionersQuery,
  useGetInClinicPractitionersQuery
} from '@/store/slices/api/patientsApiSlice';
import InfoMessage from '@/components/InfoMessage';

import ImplantingEPSelect from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/CareTeamDetails/components/ImplantingEPSelect';
import PractitionerSelect from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/CareTeamDetails/components/PractitionerSelect';
import ReferringProviderSelect from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/CareTeamDetails/components/ReferringProviderSelect';
import { useGetOrgByIdQuery } from '@/store/slices/api/organizationApiSlice';

interface CareTeamDetailsProps {
  patientId: string;
}

const CareTeamDetails = ({ patientId }: CareTeamDetailsProps) => {
  const patientDetails = useSelector((state: RootState) => selectPatientDetails(state, patientId));

  const implantingProviderType = useGetOrgByIdQuery(patientDetails?.clinic?.id ?? '', {
    skip: !patientDetails?.clinic?.id
  }).data?.organization?.parent?.implantingProviderType;

  const [followingPractitionerId, setFollowingPractitionerId] = useState<string | null>(null);
  const [inClinicPractitionerId, setInClinicPractitionerId] = useState<string | null>(null);

  const { data: patientPractitioners, isLoading: isPatientPractitionersLoading } =
    useGetAuthorizedPractitionersQuery(
      {
        patientId
      },
      {
        skip: !patientId
      }
    );

  const { data: inClinicPractitioners, isLoading: isInClinicPractitionersLoading } =
    useGetInClinicPractitionersQuery(
      {
        patientId
      },
      {
        skip: !patientId
      }
    );

  useEffect(() => {
    setFollowingPractitionerId(patientDetails?.practitioner?.id ?? null);
    setInClinicPractitionerId(patientDetails?.inClinicPractitioner?.userId ?? null);
  }, [patientDetails?.practitioner?.id, patientDetails?.inClinicPractitioner?.userId, patientId]);

  if (!patientDetails) {
    return (
      <InfoMessage
        title={'Failed To Load Care Team Details'}
        message={'Please refresh the page or contact support if the issue persists.'}
      />
    );
  }

  return (
    <Grid>
      <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
        <PractitionerSelect
          label="Following Practitioner"
          value={followingPractitionerId}
          patientId={patientId}
          isReportEditMode={true}
          patientPractitioners={patientPractitioners}
          isPatientPractitionersLoading={isPatientPractitionersLoading}
          updateField={{ patient: 'practitioner' }}
          onUpdate={setFollowingPractitionerId}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
        <ReferringProviderSelect
          clinicId={patientDetails?.clinic.id}
          patientId={patientId}
          referringPhysician={patientDetails?.referringPhysician}
          referringProvider={patientDetails?.referringProvider}
          isReportEditMode={true}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
        <PractitionerSelect
          label="In Clinic Practitioner"
          value={inClinicPractitionerId}
          patientId={patientId}
          isReportEditMode={true}
          patientPractitioners={inClinicPractitioners}
          isPatientPractitionersLoading={isInClinicPractitionersLoading}
          updateField={{ patient: 'inClinicPractitioner' }}
          onUpdate={setInClinicPractitionerId}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
        <ImplantingEPSelect
          patientId={patientId}
          implantingEp={patientDetails?.implantingEp}
          implantingProviderType={implantingProviderType as any}
          implantingEpFreeText={patientDetails?.implantingEpFreeText} />
      </Grid.Col>
    </Grid>
  );
};

export default CareTeamDetails;
